import type { IcasFeatureProperties } from './types';

/**
 * The DBF column names are preserved verbatim through ogr2ogr. Some names
 * are truncated (DBF caps at 10 chars), some carry stray punctuation
 * ("Suprafata,"), and one is mojibake'd because the .dbf encoding was
 * read as latin-1 ("VÃ¢rsta" -> Vârsta). We accept any plausible spelling
 * and surface the first non-null match.
 */
function getProp(props: IcasFeatureProperties, ...keys: string[]): string | null {
  for (const k of keys) {
    const v = props[k];
    if (v === null || v === undefined) continue;
    const s = typeof v === 'string' ? v.trim() : String(v);
    if (s.length === 0) continue;
    return s;
  }
  return null;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function row(label: string, value: string | null): string {
  if (!value) return '';
  return `
    <tr>
      <td style="padding:2px 6px 2px 0;color:#64748b;font-size:11px;white-space:nowrap;vertical-align:top;">${escapeHtml(label)}</td>
      <td style="padding:2px 0;color:#0f172a;font-size:12px;">${escapeHtml(value)}</td>
    </tr>`;
}

function popupShell(title: string, body: string): string {
  return `
    <div style="min-width:220px;max-width:320px;font-family:inherit;">
      <div style="font-weight:600;font-size:13px;color:#15803d;margin-bottom:4px;">${escapeHtml(title)}</div>
      <table style="border-collapse:collapse;width:100%;">${body}</table>
    </div>`;
}

export function entitatiPopupHtml(props: IcasFeatureProperties): string {
  const name = getProp(props, 'Entitatea', 'entitatea') ?? '—';
  const addr = getProp(props, 'Adresa', 'adresa');
  const email = getProp(props, 'E_mail', 'e_mail', 'email');
  const phone = getProp(props, 'Contacte', 'contacte');
  return popupShell(
    name,
    row('Adresă', addr) +
      row('Email', email) +
      row('Contacte', phone),
  );
}

export function trupuriPopupHtml(props: IcasFeatureProperties): string {
  const trup = getProp(props, 'Trupul', 'trupul') ?? '—';
  const supra = getProp(props, 'Suprafata', 'suprafata');
  const proprietat = getProp(props, 'Proprietat', 'proprietat');
  const up = getProp(props, 'UP', 'up');
  const id = getProp(props, 'ID', 'id');
  const suprafataNice = supra ? `${Number(supra).toFixed(2)} ha` : null;
  return popupShell(
    `Trup: ${trup}`,
    row('Suprafață', suprafataNice) +
      row('Proprietate', proprietat) +
      row('UP', up) +
      row('ID', id),
  );
}

/**
 * Stands popup — the species inventory layer carries the richest schema.
 * Field names are best-effort because DBF truncation + ogr2ogr mojibake
 * means we can't assume one canonical spelling.
 */
export function standsPopupHtml(props: IcasFeatureProperties): string {
  const ua = getProp(props, 'Unitatea a', 'Unitateaa', 'unitatea_a', 'unitatea') ?? '—';
  const supra = getProp(props, 'Suprafata,', 'Suprafata', 'suprafata');
  const varsta = getProp(props, 'VÃ¢rsta', 'Varsta', 'Vârsta', 'varsta');
  const consistenta = getProp(props, 'Consistent', 'Consistenta', 'consistenta');
  const compozitia = getProp(props, 'Compozitia', 'compozitia');
  const specia = getProp(props, 'Specia', 'specia');
  const clp = getProp(props, 'CLP', 'clp');
  const lp = getProp(props, 'LP', 'lp');
  const inclinarea = getProp(props, 'Inclinarea', 'inclinarea');
  const sol = getProp(props, 'Sol', 'sol');
  const eroziunea = getProp(props, 'Eroziunea', 'eroziunea');
  const tst = getProp(props, 'TST', 'tst');
  const tp = getProp(props, 'TP', 'tp');
  const flora = getProp(props, 'Flora', 'flora');
  const up = getProp(props, 'UP', 'up');
  const proprietat = getProp(props, 'Proprietat', 'proprietat');
  const data = getProp(props, 'Data', 'data');

  const suprafataNice = supra ? `${Number(supra).toFixed(4)} ha` : null;

  return popupShell(
    `UA ${ua}`,
    row('Compoziție', compozitia) +
      row('Specia', specia) +
      row('Vârsta', varsta ? `${varsta} ani` : null) +
      row('Consistență', consistenta) +
      row('Suprafață', suprafataNice) +
      row('CLP / LP', clp || lp ? `${clp ?? ''} / ${lp ?? ''}` : null) +
      row('Înclinare', inclinarea) +
      row('Sol', sol) +
      row('Eroziune', eroziunea) +
      row('TST', tst) +
      row('TP', tp) +
      row('Flora', flora) +
      row('UP', up) +
      row('Proprietate', proprietat) +
      row('Data', data),
  );
}
